import React, { useRef, useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPaperPlane, faTimes, faImage, faVideo, faBolt } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import EmojiPickerPopover from './EmojiPickerPopover';

const SendMessage = ({
  inputText,
  setInputText,
  onSendMessage,
  selectedFile,
  setSelectedFile,
  filePreview,
  setFilePreview,
  isSending,
  commands = [],
  inputRef: externalInputRef
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const internalInputRef = useRef(null);
  const textInputRef = externalInputRef || internalInputRef;
  const suggestionsRef = useRef(null);

  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  // Cerrar emoji picker al hacer clic afuera
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = e => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Normalizar los comandos recibidos para soportar tanto formato de BD (vComando/vTexto/vTitulo) como local (label/text)
  const normalizedCommands = (commands || []).map(cmd => ({
    command: cmd.vComando || cmd.label || '',
    title: cmd.vTitulo || cmd.title || cmd.label || '',
    text: cmd.vTexto || cmd.text || ''
  }));

  // Auto-ajuste de altura dinámico para el textarea (Shift + Enter multilínea)
  const adjustHeight = () => {
    const el = textInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const newH = Math.min(Math.max(scrollH, 38), 130);
    el.style.height = `${newH}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [inputText]);

  // Detectar trigger '/' cuando el usuario escribe
  useEffect(() => {
    if (!inputText) {
      setShowSuggestions(false);
      return;
    }

    // Busca si al final del texto hay un slash con posibles caracteres (ej: "/sal")
    const match = inputText.match(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/);

    if (match !== null) {
      const query = match[1].toLowerCase();
      const matches = normalizedCommands.filter(cmd => {
        const cmdClean = cmd.command.replace(/^\//, '').toLowerCase();
        const titleClean = cmd.title.toLowerCase();
        return cmdClean.includes(query) || titleClean.includes(query);
      });

      if (matches.length > 0) {
        setFilteredCommands(matches);
        setSelectedIndex(0);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [inputText, commands]);

  const applyCommand = cmd => {
    // Reemplaza el último "/query" en el texto por el contenido completo del comando
    const updated = inputText.replace(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/, (match) => {
      const startsWithSpace = match.startsWith(' ');
      return (startsWithSpace ? ' ' : '') + cmd.text;
    });

    setInputText(updated);
    setShowSuggestions(false);
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
        const len = updated.length;
        textInputRef.current.setSelectionRange?.(len, len);
      }
    }, 20);
  };

  // Helper para envolver texto seleccionado o insertar formato WhatsApp (*negrita*, _cursiva_, ~tachado~, ```código```)
  const applyFormatWrapper = (prefix, suffix = prefix, defaultPlaceholder = 'texto') => {
    const el = textInputRef.current;
    if (!el) return;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const selected = inputText.substring(start, end);
    const content = selected || defaultPlaceholder;
    const replacement = `${prefix}${content}${suffix}`;
    const newText = inputText.substring(0, start) + replacement + inputText.substring(end);
    setInputText(newText);
    setTimeout(() => {
      el.focus();
      if (selected) {
        el.setSelectionRange(start, start + replacement.length);
      } else {
        el.setSelectionRange(start + prefix.length, start + prefix.length + defaultPlaceholder.length);
      }
    }, 20);
  };

  // Helper para insertar emoji o icono en la posición actual del cursor
  const insertEmoji = emoji => {
    const el = textInputRef.current;
    if (!el) {
      setInputText(prev => prev + emoji);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const newText = inputText.substring(0, start) + emoji + inputText.substring(end);
    setInputText(newText);
    setTimeout(() => {
      el.focus();
      const nextPos = start + emoji.length;
      el.setSelectionRange(nextPos, nextPos);
    }, 20);
  };

  const handleKeyDown = e => {
    // Atajos de teclado para formato rápido tipo WhatsApp
    if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      applyFormatWrapper('*', '*', 'negrita');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'i' || e.key === 'I')) {
      e.preventDefault();
      applyFormatWrapper('_', '_', 'cursiva');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      applyFormatWrapper('~', '~', 'tachado');
      return;
    }

    if (showSuggestions && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyCommand(filteredCommands[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }

    // Al presionar Enter:
    // - Si lleva Shift presionado: salto de línea nativo en el textarea
    // - Si NO lleva Shift presionado: enviar el mensaje directamente
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreview({ url, type: 'image', name: file.name });
    } else if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setFilePreview({ url, type: 'video', name: file.name });
    } else {
      setFilePreview({ url: null, type: 'file', name: file.name });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (filePreview?.url) {
      URL.revokeObjectURL(filePreview.url);
    }
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    textInputRef.current?.focus();
  };

  const handlePaste = e => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          setSelectedFile(file);
          const url = URL.createObjectURL(file);
          setFilePreview({ url, type: 'image', name: file.name || 'imagen_pegada.png' });
          break;
        }
      }
    }
  };

  const handleSubmit = e => {
    if (e) e.preventDefault();
    if (showSuggestions) {
      setShowSuggestions(false);
    }
    setShowEmojiPicker(false);
    onSendMessage(e);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 10);
  };

  return (
    <div
      className="border-top position-relative"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* Popover / Menú flotante de Slash Commands al escribir / */}
      {showSuggestions && filteredCommands.length > 0 && (
        <div
          ref={suggestionsRef}
          className="position-absolute border rounded-3 shadow-lg p-1"
          style={{
            bottom: '100%',
            left: '16px',
            right: '16px',
            marginBottom: '8px',
            maxHeight: '230px',
            overflowY: 'auto',
            zIndex: 1050,
            backgroundColor: isDark ? '#182635' : '#ffffff',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <div className="px-2 py-1 text-muted d-flex align-items-center justify-content-between border-bottom mb-1" style={{ fontSize: '0.72rem' }}>
            <span className="d-flex align-items-center gap-1 fw-semibold text-success">
              <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.7rem' }} /> Atajos disponibles
            </span>
            <span>Usa ↑ ↓ y pulsa <strong>Enter</strong> o <strong>Tab</strong> para elegir</span>
          </div>
          {filteredCommands.map((cmd, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <div
                key={cmd.command + idx}
                onClick={() => applyCommand(cmd)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`d-flex align-items-center justify-content-between p-2 rounded-2 cursor-pointer transition-all ${isSelected ? 'bg-success text-white' : 'hover-bg-light text-dark'
                  }`}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#065f46' : 'transparent'
                }}
              >
                <div className="d-flex align-items-center gap-2 overflow-hidden">
                  <span
                    className={`badge ${isSelected ? 'bg-white text-dark' : 'bg-light text-success border'}`}
                    style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {cmd.command.startsWith('/') ? cmd.command : `/${cmd.command}`}
                  </span>
                  <div className="d-flex flex-column text-truncate">
                    <span className={`fw-semibold ${isSelected ? 'text-white' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                      {cmd.title}
                    </span>
                    <small
                      className={`text-truncate ${isSelected ? 'text-white-50' : 'text-muted'}`}
                      style={{ fontSize: '0.73rem', maxWidth: '420px' }}
                    >
                      {cmd.text}
                    </small>
                  </div>
                </div>
                {isSelected && (
                  <span className="badge bg-white bg-opacity-25 text-white" style={{ fontSize: '0.68rem' }}>
                    Enter ↵
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* File Preview Bar */}
      {filePreview && (
        <div
          className="p-2 px-3 border-bottom d-flex align-items-center justify-content-between"
          style={{
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <div className="d-flex align-items-center gap-2 overflow-hidden">
            {filePreview.type === 'image' && filePreview.url ? (
              <img
                src={filePreview.url}
                alt="Preview"
                style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '6px' }}
              />
            ) : filePreview.type === 'video' ? (
              <div className="bg-dark text-white rounded d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                <FontAwesomeIcon icon={faVideo} />
              </div>
            ) : (
              <div className="bg-secondary text-white rounded d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                <FontAwesomeIcon icon={faImage} />
              </div>
            )}
            <div className="d-flex flex-column">
              <span className="fw-semibold text-truncate" style={{ fontSize: '0.82rem', maxWidth: '300px' }}>
                {filePreview.name}
              </span>
              <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                {filePreview.type === 'image' ? 'Foto lista para enviar' : 'Video/Archivo listo'}
              </small>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-1"
            onClick={handleRemoveFile}
            title="Quitar archivo"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Barra de formato rápido y emojis de WhatsApp */}
      <div
        className="px-3 pt-2 pb-1 d-flex align-items-center justify-content-between"
        style={{
          borderBottom: isDark ? '1px solid #1e293b' : '1px solid #f1f5f9',
          fontSize: '0.75rem',
          color: isDark ? '#94a3b8' : '#64748b'
        }}
      >
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            className="btn btn-sm py-0 px-2 rounded border-0"
            style={{
              fontWeight: 700,
              color: isDark ? '#cbd5e1' : '#334155',
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
              minWidth: '24px',
              height: '24px'
            }}
            title="Negrita (*texto*) - Ctrl+B"
            onClick={() => applyFormatWrapper('*', '*', 'negrita')}
          >
            B
          </button>
          <button
            type="button"
            className="btn btn-sm py-0 px-2 rounded border-0 fst-italic"
            style={{
              fontFamily: 'serif',
              fontWeight: 600,
              color: isDark ? '#cbd5e1' : '#334155',
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
              minWidth: '24px',
              height: '24px'
            }}
            title="Cursiva (_texto_) - Ctrl+I"
            onClick={() => applyFormatWrapper('_', '_', 'cursiva')}
          >
            I
          </button>
          <button
            type="button"
            className="btn btn-sm py-0 px-2 rounded border-0 text-decoration-line-through"
            style={{
              fontWeight: 600,
              color: isDark ? '#cbd5e1' : '#334155',
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
              minWidth: '24px',
              height: '24px'
            }}
            title="Tachado (~texto~) - Ctrl+Shift+X"
            onClick={() => applyFormatWrapper('~', '~', 'tachado')}
          >
            S
          </button>
          <button
            type="button"
            className="btn btn-sm py-0 px-2 rounded border-0 font-monospace"
            style={{
              fontWeight: 600,
              fontSize: '0.72rem',
              color: isDark ? '#cbd5e1' : '#334155',
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
              minWidth: '24px',
              height: '24px'
            }}
            title="Monoespaciado (```texto```)"
            onClick={() => applyFormatWrapper('```', '```', 'código')}
          >
            &lt;/&gt;
          </button>
          <div className="vr mx-1" style={{ height: '14px', opacity: 0.3 }} />
          <button
            type="button"
            className="btn btn-sm py-0 px-2 rounded border-0"
            style={{
              color: isDark ? '#cbd5e1' : '#334155',
              backgroundColor: showEmojiPicker ? (isDark ? '#334155' : '#e2e8f0') : (isDark ? '#1e293b' : '#f1f5f9'),
              minWidth: '26px',
              height: '24px'
            }}
            title="Insertar emojis e iconos de promoción"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            😊
          </button>
        </div>

        <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
          <kbd style={{ fontSize: '0.66rem', backgroundColor: isDark ? '#1e293b' : '#e2e8f0', color: isDark ? '#94a3b8' : '#475569' }}>Shift + Enter</kbd> salto de línea
        </span>
      </div>

      {/* Selector completo de Emojis de WhatsApp (Caritas, Akasia POS/Ventas, Símbolos, Promociones) */}
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="position-absolute shadow-lg"
          style={{
            bottom: '100%',
            left: '16px',
            marginBottom: '8px',
            zIndex: 1060
          }}
        >
          <EmojiPickerPopover
            onSelectEmoji={insertEmoji}
            onClose={() => setShowEmojiPicker(false)}
            isDark={isDark}
          />
        </div>
      )}

      {/* Formulario de redacción de mensaje */}
      <form onSubmit={handleSubmit} className="p-2 px-3 d-flex align-items-end gap-2">
        <input
          type="file"
          ref={fileInputRef}
          hidden
          accept="image/*,video/*,application/pdf"
          onChange={handleFileChange}
        />

        {/* Textarea multilínea con crecimiento automático y soporte Shift+Enter */}
        <textarea
          ref={textInputRef}
          rows={1}
          className="form-control px-3 py-2"
          style={{
            backgroundColor: isDark ? '#182635' : '#f1f5f9',
            color: isDark ? '#f9fafd' : '#0f172a',
            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
            fontSize: '0.88rem',
            lineHeight: '1.4',
            borderRadius: '16px',
            resize: 'none',
            overflowY: 'auto',
            minHeight: '38px',
            maxHeight: '130px'
          }}
          placeholder={selectedFile ? 'Escribe un pie de foto (opcional)...' : 'Escribe un mensaje o escribe "/" para atajos...'}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          autoFocus
        />

        {/* Botón Agregar (+) para multimedia */}
        <button
          type="button"
          className="btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-none flex-shrink-0"
          style={{
            fontSize: '0.84rem',
            fontWeight: 500,
            height: '38px',
            color: isDark ? '#e2e8f0' : '#334155',
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
            whiteSpace: 'nowrap'
          }}
          title="Agregar foto, video o archivo"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.85rem', color: isDark ? '#34d399' : '#065f46' }} />
          <span>Agregar</span>
        </button>

        {/* Botón Enviar */}
        <button
          type="submit"
          className="btn rounded-circle d-flex align-items-center justify-content-center border-0 text-white flex-shrink-0"
          style={{ width: '38px', height: '38px', backgroundColor: '#065f46' }}
          title="Enviar mensaje (Enter)"
          disabled={isSending || (!inputText.trim() && !selectedFile)}
        >
          {isSending ? (
            <div className="spinner-border spinner-border-sm text-white" role="status"></div>
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '0.9rem' }} />
          )}
        </button>
      </form>
    </div>
  );
};

export default SendMessage;
